import React, { createContext, useContext, useEffect, useState } from 'react'
import { authAdapter } from '../app/authAdapter'

type Role = 'PV_USER' | 'PV_ADMIN'

type User = { email?: string; id?: string | number; fullName?: string; roles?: Role[] }

type AuthContextValue = {
  isAuthenticated: boolean
  user: User | null
  loading: boolean
  error: string | null
  signInWithEmailPassword: (email: string, password: string) => Promise<any>
  signInWithGoogle: (idToken: string) => Promise<any>
  signOut: () => void
  touch: () => void
}

const AuthCtx = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const raw = localStorage.getItem('auth_user')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastActive, setLastActive] = useState(Date.now())

  const hasToken = (() => {
    try { return !!localStorage.getItem('auth_token') } catch { return false }
  })()

  useEffect(() => {
    const interval = setInterval(() => {
      const timeoutMs = 30 * 60 * 1000
      if (user && Date.now() - lastActive > timeoutMs) {
        setUser(null)
        localStorage.removeItem('auth_token')
      }
    }, 60 * 1000)
    return () => clearInterval(interval)
  }, [user, lastActive])

  const signInWithEmailPassword: AuthContextValue['signInWithEmailPassword'] = async (email, password) => {
    setLoading(true)
    setError(null)
    try {
      const data = await authAdapter.loginWithEmailPassword(email, password)
      const u = {
        id: data.userProfile?.userId ?? data.userProfile?.id,
        fullName: data.userProfile?.fullName
      }
      setUser(u)
      try { localStorage.setItem('auth_user', JSON.stringify(u)) } catch { }
      return data
    } catch (e) {
      const msg = (e as any)?.response?.data?.message || 'Login failed'
      setError(msg)
      throw e
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle: AuthContextValue['signInWithGoogle'] = async (idToken) => {
    setLoading(true)
    setError(null)
    try {
      const data = await authAdapter.loginWithGoogle(idToken)
      const u = { id: data.userProfile?.userId ?? data.userProfile?.id, fullName: data.userProfile?.fullName }
      setUser(u)
      try { localStorage.setItem('auth_user', JSON.stringify(u)) } catch { }
      return data
    } catch (e) {
      const msg = (e as any)?.response?.data?.message || 'Login failed'
      setError(msg)
      throw e
    } finally {
      setLoading(false)
    }
  }

  const signOut = () => {
    setUser(null)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('auth_user')
  }

  const value: AuthContextValue = {
    isAuthenticated: !!user || hasToken,
    user,
    loading,
    error,
    signInWithEmailPassword,
    signInWithGoogle,
    signOut,
    touch: () => setLastActive(Date.now())
  }
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('AuthContext missing')
  return ctx
}
